// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components
import { BaseInput, BaseLabel, Loading, BaseAutocomplete } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { schema, defaultValues } from './helper';
import { IBaseOption, IBodyUpdate, IClientContact } from '~/utils/interface/common';
import { createFormData, formatPhoneNumber, removeNonDigits } from '~/utils/helper/common';
import { EMPTY_STRING, optionContactTypeClient } from '~/utils/constants/common';
import { updateClient } from '~/thunks/client/clientThunk';
import { clientActions } from '~/thunks/client/clientSlice';
// Styles, images, icons
import styles from './ClientContactForm.module.scss';

type Props = {
  listContact?: IClientContact[];
  dataDefault?: IClientContact;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const ClientContactForm = (props: Props) => {
  //#region Destructuring Props
  const { listContact, dataDefault, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const { id: clientId } = useParams();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<IClientContact>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValues,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook

  useEffect(() => {
    if (!dataDefault) return;

    reset({
      firstName: dataDefault?.firstName || EMPTY_STRING,
      email: dataDefault?.email,
      phoneNumber: dataDefault?.phoneNumber || EMPTY_STRING,
      contactType: dataDefault?.contactType || EMPTY_STRING,
    });
  }, [dataDefault]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCreateContact = async (data: IClientContact) => {
    setIsLoading(true);

    const body = {
      additionalContact: [
        {
          id: dataDefault?.id || undefined,
          firstName: data?.firstName,
          email: data?.email,
          phoneNumber: data?.phoneNumber && removeNonDigits(data?.phoneNumber),
          contactType: data?.contactType,
        },
      ],
    };

    const formData = await createFormData(body);

    const params: IBodyUpdate<FormData> = {
      id: clientId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateClient(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
        dispatch(clientActions.setRefreshClientDetail(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose?.();
    reset();
  };

  const handleGetOptionContactTypeClient = () => {
    const selectedRelationships = listContact?.map((c) => c.contactType) || [];

    return optionContactTypeClient.filter((opt) => !selectedRelationships.includes(opt.value));
  };
  //#endregion Handle Function

  return (
    <Dialog open={true} onClose={handleCloseModal} fullWidth maxWidth='md'>
      <DialogTitle id='taskForm' className={cx('header')}>
        <Stack direction='column' spacing={1}>
          <BaseLabel label={t(!dataDefault ? 'form_contact_title' : 'form_contact_title_edit')} styleType='bigTitle' />
        </Stack>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 container spacing={5}>
          <Grid2 size={6}>
            <Controller
              control={control}
              name='firstName'
              render={({ field }) => (
                <BaseInput
                  width={'100%'}
                  label={t('form_contact_title_label_name')}
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={errors?.firstName?.message}
                  required
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='email'
              render={({ field }) => (
                <BaseInput
                  width={'100%'}
                  label={t('client_form_title_email_contact')}
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={errors?.email?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='phoneNumber'
              render={({ field }) => (
                <BaseInput
                  width={'100%'}
                  label={t('client_form_title_phone_contact')}
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(formatPhoneNumber(event.target.value));
                  }}
                  errorMessage={errors?.phoneNumber?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'contactType'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseAutocomplete
                    width={'100%'}
                    showIcon={false}
                    placeholder={EMPTY_STRING}
                    value={optionContactTypeClient.find((item) => item.value === value) || null}
                    options={handleGetOptionContactTypeClient()}
                    textSearch={optionContactTypeClient.find((item) => item.value === value)?.label || value}
                    onTextSearchChange={onChange}
                    onChange={(option) => {
                      const data = option as IBaseOption;

                      onChange(data?.value);
                    }}
                    label={t('client_form_title_contact_type')}
                    errorMessage={errors?.contactType?.message}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          disabled={dataDefault ? Object.keys(dirtyFields).length === 0 : false}
          onClick={handleSubmit(handleCreateContact)}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default ClientContactForm;
